import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc,
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp,
  onSnapshot,
  runTransaction,
  addDoc,
  updateDoc,
  writeBatch,
  deleteDoc
} from 'firebase/firestore';
import { generateMockTransactions, initialAccounts, MOCK_TRANSACTIONS } from './mockData';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';

export async function initializeUserData(uid, email) {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    // Create user profile
    await setDoc(userRef, {
      email,
      name: email.split('@')[0],
      role: 'user',
      createdAt: serverTimestamp()
    });

    // Initialize accounts
    const accountsRef = collection(db, `users/${uid}/accounts`);
    for (const account of initialAccounts) {
      await setDoc(doc(accountsRef), {
        ...account,
        createdAt: serverTimestamp()
      });
    }

    // Initialize mock transactions
    const transactionsRef = collection(db, 'transactions');
    const mockTransactions = generateMockTransactions();
    
    for (const transaction of mockTransactions) {
      await setDoc(doc(transactionsRef), {
        ...transaction,
        userId: uid,
        createdAt: serverTimestamp(),
        date: transaction.date
      });
    }
  }
}

export async function getUserData(uid) {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) {
    throw new Error('User not found');
  }
  return userDoc.data();
}

export async function getRecentTransactions(uid) {
  // Get real transactions from Firestore
  const transactionsRef = collection(db, 'transactions');
  const q = query(
    transactionsRef,
    where('userId', '==', uid),
    orderBy('date', 'desc'),
    limit(20)
  );
  
  const querySnapshot = await getDocs(q);
  const realTransactions = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    date: doc.data().date.toDate()
  }));

  // Combine mock and real transactions
  const allTransactions = [...realTransactions, ...MOCK_TRANSACTIONS]
    .sort((a, b) => b.date - a.date);

  return allTransactions;
}

export async function getAccountBalances(uid) {
  const accountsDoc = await getDoc(doc(db, 'users', uid));
  if (!accountsDoc.exists()) {
    throw new Error('User accounts not found');
  }
  return accountsDoc.data().accounts || [];
}

// Real-time updates for account balances
export function subscribeToAccountUpdates(uid, callback) {
  const userDoc = doc(db, 'users', uid);
  return onSnapshot(userDoc, (doc) => {
    if (doc.exists()) {
      callback(doc.data().accounts || []);
    }
  });
}

// Real-time updates for transactions
export function subscribeToTransactions(userId, callback) {
  console.log('Setting up transaction subscription for userId:', userId);
  
  // Set up real-time listener for actual transactions
  const transactionsRef = collection(db, 'transactions');
  const q = query(
    transactionsRef,
    where('userId', '==', userId),
    orderBy('date', 'desc')
  );

  const unsubscribe = onSnapshot(q, 
    (querySnapshot) => {
      console.log('Got transaction snapshot, size:', querySnapshot.size);
      
      const realTransactions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        realTransactions.push({
          id: doc.id,
          ...data,
          date: data.date?.toDate() || new Date()
        });
      });

      console.log('Real transactions:', realTransactions);
      callback(realTransactions);
    },
    (error) => {
      console.error('Transaction subscription error:', error);
    }
  );

  return unsubscribe;
}

// Generate a proper bank account number
const generateBankAccountNumber = () => {
  // First digit should be non-zero (1-9)
  const firstDigit = Math.floor(1 + Math.random() * 9);
  
  // Generate remaining 9 digits (for a 10-digit account number)
  const remainingDigits = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
  
  // Combine first digit with remaining digits
  const accountNumber = `${firstDigit}${remainingDigits}`;
  
  // Format the number with spaces for readability (optional)
  // return accountNumber.replace(/(\d{3})(?=\d)/g, '$1 ');
  
  return accountNumber;
};

// Update existing user's account numbers if they have asterisks
export const updateAccountNumbers = async (uid) => {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);
  
  if (userDoc.exists()) {
    const userData = userDoc.data();
    let needsUpdate = false;
    
    const updatedAccounts = userData.accounts.map(account => {
      // Check if account number has asterisks or needs to be updated
      if (!account.accountNumber || account.accountNumber.includes('*')) {
        needsUpdate = true;
        return {
          ...account,
          accountNumber: generateBankAccountNumber()
        };
      }
      return account;
    });

    if (needsUpdate) {
      await updateDoc(userRef, {
        accounts: updatedAccounts
      });
    }

    return updatedAccounts;
  }
};

export async function setupInitialUserData(uid, email) {
  const userRef = doc(db, 'users', uid);
  
  // Check if user document already exists
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) {
    // Create initial user data
    await setDoc(userRef, {
      name: email.split('@')[0], // Default name from email
      email: email,
      role: 'user',
      accounts: [
        {
          type: "Checking",
          accountNumber: generateBankAccountNumber(),
          balance: 36750.60,
          available: 2500.00,
          routingNumber: "021000021"
        },
        {
          type: "Savings",
          accountNumber: generateBankAccountNumber(),
          balance: 10230.40,
          available: 10000.00,
          routingNumber: "021000021"
        }
      ]
    });
    
    console.log('Initial user data created successfully');
  } else {
    // If user exists, ensure account numbers are properly formatted
    await updateAccountNumbers(uid);
  }
}

export async function processTransfer(transferData) {
  const { 
    fromAccount, 
    toAccount, 
    amount, 
    reference, 
    transferType,
    userId,
    recipientEmail,
    accountName,
    toBank
  } = transferData;

  try {
    await runTransaction(db, async (transaction) => {
      // Get source user document
      const userRef = doc(db, 'users', userId);
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const accounts = [...userData.accounts];
      const sourceAccountIndex = accounts.findIndex(acc => acc.type === fromAccount);

      if (sourceAccountIndex === -1) {
        throw new Error('Source account not found');
      }

      if (accounts[sourceAccountIndex].balance < amount) {
        throw new Error('Insufficient funds');
      }

      // Update source account
      accounts[sourceAccountIndex].balance -= amount;
      accounts[sourceAccountIndex].available -= amount;

      // Handle different transfer types
      let description = '';
      let recipientDescription = '';
      let recipientDocs = null;

      switch (transferType) {
        case 'internal':
          // Internal transfer between own accounts
          const destAccountIndex = accounts.findIndex(acc => acc.type === toAccount);
          if (destAccountIndex === -1) {
            throw new Error('Destination account not found');
          }
          accounts[destAccountIndex].balance += amount;
          accounts[destAccountIndex].available += amount;
          description = `Transfer to ${toAccount}: ${reference}`;
          recipientDescription = `Transfer from ${fromAccount}: ${reference}`;
          break;

        case 'user':
          // Transfer to another Northstone user
          const recipientQuery = query(
            collection(db, 'users'),
            where('email', '==', recipientEmail)
          );
          recipientDocs = await getDocs(recipientQuery);
          
          if (recipientDocs.empty) {
            throw new Error('Recipient not found');
          }

          const recipientRef = doc(db, 'users', recipientDocs.docs[0].id);
          const recipientData = recipientDocs.docs[0].data();
          
          // Update recipient's default account (assuming Checking)
          const recipientAccounts = [...recipientData.accounts];
          const recipientAccountIndex = recipientAccounts.findIndex(acc => acc.type === 'Checking');
          
          if (recipientAccountIndex === -1) {
            throw new Error('Recipient account not found');
          }

          recipientAccounts[recipientAccountIndex].balance += amount;
          recipientAccounts[recipientAccountIndex].available += amount;

          transaction.update(recipientRef, { accounts: recipientAccounts });
          
          description = `Transfer to ${recipientEmail}: ${reference}`;
          recipientDescription = `Transfer from ${userData.email}: ${reference}`;
          break;

        case 'external':
          // External bank transfer
          description = `Transfer to ${toBank} (${accountName}): ${reference}`;
          break;

        default:
          throw new Error('Invalid transfer type');
      }

      // Update source user's accounts
      transaction.update(userRef, { accounts });

      // Create transaction records
      const transactionsRef = collection(db, 'transactions');
      
      // Debit transaction for source account
      await addDoc(transactionsRef, {
        userId,
        accountType: fromAccount,
        type: 'debit',
        amount,
        description,
        date: serverTimestamp(),
        category: 'Transfer',
        status: 'Completed'
      });

      // Credit transaction for recipient (if internal or user transfer)
      if (transferType !== 'external') {
        const recipientUserId = transferType === 'internal' ? userId : recipientDocs.docs[0].id;
        await addDoc(transactionsRef, {
          userId: recipientUserId,
          accountType: transferType === 'internal' ? toAccount : 'Checking',
          type: 'credit',
          amount,
          description: recipientDescription,
          date: serverTimestamp(),
          category: 'Transfer',
          status: 'Completed'
        });
      }
    });

    return true;
  } catch (error) {
    console.error('Transfer error:', error);
    throw error;
  }
}

export async function processBillPayment(paymentData) {
  const {
    fromAccount,
    provider,
    accountNumber,
    amount,
    scheduleDate,
    memo,
    userId
  } = paymentData;

  try {
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const accounts = [...userData.accounts];
      const sourceAccountIndex = accounts.findIndex(acc => acc.type === fromAccount);

      if (sourceAccountIndex === -1) {
        throw new Error('Source account not found');
      }

      // Create bill payment record
      const billPaymentsRef = collection(db, 'billPayments');
      await addDoc(billPaymentsRef, {
        userId,
        fromAccount,
        provider,
        accountNumber,
        amount,
        scheduleDate,
        memo,
        status: 'Scheduled',
        createdAt: serverTimestamp()
      });

      // Create pending transaction
      const transactionsRef = collection(db, 'transactions');
      await addDoc(transactionsRef, {
        userId,
        accountType: fromAccount,
        type: 'debit',
        amount,
        description: `Bill Payment - ${provider}`,
        date: serverTimestamp(),
        category: 'Bill Pay',
        status: 'Pending',
        scheduledDate: scheduleDate
      });
    });

    return true;
  } catch (error) {
    console.error('Bill payment error:', error);
    throw error;
  }
}

export async function processMobileDeposit(depositData) {
  const {
    toAccount,
    amount,
    frontImage,
    backImage,
    userId
  } = depositData;

  try {
    // Create deposit record
    const depositsRef = collection(db, 'mobileDeposits');
    const depositRef = await addDoc(depositsRef, {
      userId,
      toAccount,
      amount,
      status: 'Pending Review',
      createdAt: serverTimestamp(),
      reviewedAt: null,
      imageUrls: {
        front: null,
        back: null
      }
    });

    // Upload images to storage (implementation depends on your storage setup)
    // const frontImageUrl = await uploadImage(frontImage, `deposits/${depositRef.id}/front`);
    // const backImageUrl = await uploadImage(backImage, `deposits/${depositRef.id}/back`);

    // Create pending transaction
    const transactionsRef = collection(db, 'transactions');
    await addDoc(transactionsRef, {
      userId,
      accountType: toAccount,
      type: 'credit',
      amount,
      description: 'Mobile Deposit',
      date: serverTimestamp(),
      category: 'Deposit',
      status: 'Pending',
      depositId: depositRef.id
    });

    return true;
  } catch (error) {
    console.error('Mobile deposit error:', error);
    throw error;
  }
}

// Update createNewUserWithAccounts to use the new generator
export const createNewUserWithAccounts = async ({ email, password, name, phone, address, role, accounts }) => {
  try {
    const adminAuth = auth.currentUser;
    const newUserCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = newUserCredential.user.uid;
    await auth.updateCurrentUser(adminAuth);

    const defaultAccounts = [
      {
        type: 'Checking',
        balance: Number(accounts[0].balance || 0),
        available: Number(accounts[0].available || 0),
        accountNumber: generateBankAccountNumber(),
        routingNumber: '021000021',
        id: 'checking-' + Date.now(),
        createdAt: new Date().toISOString()
      },
      {
        type: 'Savings',
        balance: Number(accounts[1].balance || 0),
        available: Number(accounts[1].available || 0),
        accountNumber: generateBankAccountNumber(),
        routingNumber: '021000021',
        id: 'savings-' + Date.now(),
        createdAt: new Date().toISOString()
      }
    ];

    await setDoc(doc(db, 'users', userId), {
      email,
      name: name || email.split('@')[0],
      phone,
      address: {
        street: address.street || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zipCode || ''
      },
      role: role || 'user',
      status: 'active',
      createdAt: serverTimestamp(),
      createdBy: adminAuth.email,
      accounts: defaultAccounts
    });

    return userId;
  } catch (error) {
    console.error('Error creating new user:', error);
    throw error;
  }
};

// Initialize accounts for existing users if needed
export const initializeUserAccounts = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      if (!userData.accounts || userData.accounts.length === 0) {
        // Set up default accounts
        const defaultAccounts = [
          {
            type: 'Checking',
            balance: 0,
            available: 0,
            accountNumber: Math.floor(10000000 + Math.random() * 90000000).toString(),
            routingNumber: '021000021',
            id: 'checking-' + Date.now(),
            createdAt: new Date().toISOString()
          },
          {
            type: 'Savings',
            balance: 0,
            available: 0,
            accountNumber: Math.floor(10000000 + Math.random() * 90000000).toString(),
            routingNumber: '021000021',
            id: 'savings-' + Date.now(),
            createdAt: new Date().toISOString()
          }
        ];

        await updateDoc(userRef, {
          accounts: defaultAccounts,
          updatedAt: serverTimestamp()
        });

        console.log('Initialized accounts for user:', userId);
      }
    }
  } catch (error) {
    console.error('Error initializing accounts:', error);
    throw error;
  }
};

// Get all users with their account balances
export const getAllUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = [];
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const accounts = userData.accounts || [];

      // Process accounts with proper number conversion
      const processedAccounts = accounts.map((account, index) => ({
        id: index.toString(), // Generate an ID since it's an array
        type: account.type,
        balance: Number(account.balance || 0),
        available: Number(account.available || 0),
        accountNumber: account.accountNumber,
        routingNumber: account.routingNumber
      }));

      // Ensure all user data fields are properly structured
      const personalInfo = userData.personalInfo || {};
      const contactDetails = userData.contactDetails || {};
      const documents = userData.documents || {};

      users.push({
        uid: userDoc.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        status: userData.status,
        accounts: processedAccounts,
        personalInfo: {
          firstName: personalInfo.firstName || '',
          lastName: personalInfo.lastName || '',
          dateOfBirth: personalInfo.dateOfBirth || null,
          nationality: personalInfo.nationality || '',
          maritalStatus: personalInfo.maritalStatus || '',
          address: personalInfo.address || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
          }
        },
        contactDetails: {
          phoneNumber: contactDetails.phoneNumber || '',
          alternativeEmail: contactDetails.alternativeEmail || '',
          emergencyContact: contactDetails.emergencyContact || {
            name: '',
            relationship: '',
            phone: ''
          }
        },
        documents: {
          idCardType: documents.idCardType || '',
          idCardNumber: documents.idCardNumber || '',
          verificationStatus: documents.verificationStatus || 'pending',
          infoRequest: documents.infoRequest || null,
          infoRequestedAt: documents.infoRequestedAt || null
        },
        registrationDate: userData.registrationDate || null,
        createdAt: userData.createdAt || null
      });
    }

    console.log('Processed users:', users);
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

// Update user account without using serverTimestamp in array
export const updateUserAccount = async (userId, accountIndex, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const accounts = [...userData.accounts];

    // Ensure we're updating the correct account
    if (!accounts[parseInt(accountIndex)]) {
      throw new Error('Account not found');
    }

    // Update the specific account in the array
    accounts[parseInt(accountIndex)] = {
      ...accounts[parseInt(accountIndex)],
      balance: Number(updates.balance),
      available: Number(updates.available),
      accountNumber: updates.accountNumber,
      routingNumber: updates.routingNumber
    };

    // Update the entire accounts array and add lastModified at root level
    await updateDoc(userRef, {
      accounts: accounts,
      lastModified: serverTimestamp() // Move timestamp to root level
    });

    console.log('Account updated successfully:', {
      userId,
      accountIndex,
      newBalance: updates.balance,
      newAvailable: updates.available,
      newAccountNumber: updates.accountNumber
    });

    return true;
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

// Update transaction (for admin)
export const updateTransaction = async (userId, transactionId, updates) => {
  try {
    const transactionRef = doc(db, 'users', userId, 'transactions', transactionId);
    
    if (updates.deleted) {
      // Soft delete - mark as deleted instead of actually deleting
      await updateDoc(transactionRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: 'admin'
      });
    } else {
      await updateDoc(transactionRef, {
        ...updates,
        lastModified: serverTimestamp(),
        modifiedBy: 'admin'
      });
    }
  } catch (error) {
    console.error('Error updating transaction:', error);
    throw error;
  }
};

// Delete transaction (for admin)
export const deleteTransaction = async (userId, transactionId) => {
  try {
    const transactionRef = doc(db, 'users', userId, 'transactions', transactionId);
    await updateDoc(transactionRef, {
      deleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: 'admin'
    });
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw error;
  }
};

// Function to promote user to admin
export const promoteToAdmin = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'admin',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error promoting user to admin:', error);
    throw error;
  }
};

// Subscribe to real-time updates
export const subscribeToUserUpdates = (callback) => {
  return onSnapshot(collection(db, 'users'), (snapshot) => {
    try {
      const users = snapshot.docs.map(doc => {
        const userData = doc.data();
        const accounts = userData.accounts || [];

        const processedAccounts = accounts.map((account, index) => ({
          id: index.toString(),
          type: account.type,
          balance: Number(account.balance || 0),
          available: Number(account.available || 0),
          accountNumber: account.accountNumber,
          routingNumber: account.routingNumber
        }));

        // Ensure all user data fields are properly structured
        const personalInfo = userData.personalInfo || {};
        const contactDetails = userData.contactDetails || {};
        const documents = userData.documents || {};

        return {
          uid: doc.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          status: userData.status,
          accounts: processedAccounts,
          personalInfo: {
            firstName: personalInfo.firstName || '',
            lastName: personalInfo.lastName || '',
            dateOfBirth: personalInfo.dateOfBirth || null,
            nationality: personalInfo.nationality || '',
            maritalStatus: personalInfo.maritalStatus || '',
            address: personalInfo.address || {
              street: '',
              city: '',
              state: '',
              postalCode: '',
              country: ''
            }
          },
          contactDetails: {
            phoneNumber: contactDetails.phoneNumber || '',
            alternativeEmail: contactDetails.alternativeEmail || '',
            emergencyContact: contactDetails.emergencyContact || {
              name: '',
              relationship: '',
              phone: ''
            }
          },
          documents: {
            idCardType: documents.idCardType || '',
            idCardNumber: documents.idCardNumber || '',
            verificationStatus: documents.verificationStatus || 'pending',
            infoRequest: documents.infoRequest || null,
            infoRequestedAt: documents.infoRequestedAt || null
          },
          registrationDate: userData.registrationDate || null,
          createdAt: userData.createdAt || null
        };
      });

      callback(users);
    } catch (error) {
      console.error('Error in subscription:', error);
    }
  });
};

export const rejectUserRegistration = async (userId, reason) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status: 'rejected',
      role: 'pending',
      rejectionReason: reason,
      rejectedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Move user to rejected applications collection
    const rejectedRef = doc(db, 'rejectedApplications', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();

    await setDoc(rejectedRef, {
      ...userData,
      rejectionReason: reason,
      rejectedAt: new Date().toISOString()
    });

    return true;
  } catch (error) {
    console.error('Error rejecting registration:', error);
    throw error;
  }
};

export const requestAdditionalInfo = async (userId, requestDetails) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'documents.verificationStatus': 'info_requested',
      'documents.infoRequest': requestDetails,
      'documents.infoRequestedAt': new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Create an info request record
    const requestRef = doc(collection(db, 'infoRequests'));
    await setDoc(requestRef, {
      userId,
      requestDetails,
      status: 'pending',
      createdAt: new Date().toISOString()
    });

    return true;
  } catch (error) {
    console.error('Error requesting additional information:', error);
    throw error;
  }
};

// Update the existing updateUserStatus function to handle verification
export const updateUserStatus = async (userId, newStatus) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();

    // Update user status
    await updateDoc(userRef, {
      status: newStatus,
      role: newStatus === 'approved' ? 'user' : 'pending',
      updatedAt: new Date().toISOString()
    });

    // If approving user, create default accounts if they don't exist
    if (newStatus === 'approved' && (!userData.accounts || userData.accounts.length === 0)) {
      const accounts = [
        {
          type: 'Checking',
          balance: 0,
          available: 0,
          accountNumber: Math.floor(10000000 + Math.random() * 90000000).toString(),
          routingNumber: '021000021',
          id: 'checking-' + Date.now(),
          createdAt: new Date().toISOString()
        },
        {
          type: 'Savings',
          balance: 0,
          available: 0,
          accountNumber: Math.floor(10000000 + Math.random() * 90000000).toString(),
          routingNumber: '021000021',
          id: 'savings-' + Date.now(),
          createdAt: new Date().toISOString()
        }
      ];

      await updateDoc(userRef, {
        accounts: accounts
      });

      // Move to approved users collection
      const approvedRef = doc(db, 'approvedUsers', userId);
      await setDoc(approvedRef, {
        ...userData,
        accounts,
        approvedAt: new Date().toISOString()
      });
    }

    return true;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

// Add new transaction (for admin)
export const addAdminTransaction = async (userId, transactionData) => {
  try {
    const transactionsRef = collection(db, 'transactions');
    
    // Format the transaction data
    const newTransaction = {
      userId,
      accountType: transactionData.accountType,
      type: transactionData.type,
      amount: Number(transactionData.amount),
      description: transactionData.description,
      date: transactionData.date ? new Date(transactionData.date) : serverTimestamp(),
      category: transactionData.category,
      status: transactionData.status || 'Completed',
      createdAt: serverTimestamp(),
      createdBy: 'admin',
      reference: transactionData.reference || ''
    };

    // Add the transaction
    const docRef = await addDoc(transactionsRef, newTransaction);

    // If this is a real transaction (not just for display), update account balance
    if (transactionData.updateBalance) {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const accounts = [...userData.accounts];
        const accountIndex = accounts.findIndex(acc => acc.type === transactionData.accountType);

        if (accountIndex !== -1) {
          const multiplier = transactionData.type === 'credit' ? 1 : -1;
          accounts[accountIndex].balance += (multiplier * Number(transactionData.amount));
          accounts[accountIndex].available += (multiplier * Number(transactionData.amount));

          await updateDoc(userRef, {
            accounts,
            lastModified: serverTimestamp()
          });
        }
      }
    }

    return docRef.id;
  } catch (error) {
    console.error('Error adding admin transaction:', error);
    throw error;
  }
};

// Delete all transactions for a user
export const deleteAllUserTransactions = async (userId) => {
  try {
    const transactionsRef = collection(db, 'transactions');
    const q = query(
      transactionsRef,
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    console.log(`Deleted ${querySnapshot.size} transactions for user:`, userId);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error deleting transactions:', error);
    throw error;
  }
};

// Find a user by email
export const findUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('email', '==', email),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      console.log('No user found with email:', email);
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error finding user:', error);
    throw error;
  }
};

// Delete specific transaction and update balance
export const deleteSpecificTransaction = async (userId, transactionId) => {
  try {
    const transactionRef = doc(db, 'transactions', transactionId);
    const transactionDoc = await getDoc(transactionRef);
    
    if (!transactionDoc.exists()) {
      throw new Error('Transaction not found');
    }

    const transactionData = transactionDoc.data();
    
    // Update account balance before deleting the transaction
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const accounts = [...userData.accounts];
      const accountIndex = accounts.findIndex(acc => acc.type === transactionData.accountType);

      if (accountIndex !== -1) {
        // Reverse the original transaction's effect on the balance
        const multiplier = transactionData.type === 'credit' ? -1 : 1;
        accounts[accountIndex].balance += (multiplier * Number(transactionData.amount));
        accounts[accountIndex].available += (multiplier * Number(transactionData.amount));

        // Update user's account balances
        await updateDoc(userRef, {
          accounts,
          lastModified: serverTimestamp()
        });
      }
    }

    // Delete the transaction
    await deleteDoc(transactionRef);
    
    return {
      success: true,
      message: 'Transaction deleted and balance updated successfully'
    };
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw error;
  }
}; 